import jsPDF from "jspdf";
import "jspdf-autotable";
import { gql } from "@apollo/client";

interface PrintableColumn {
  header: string;
  key: string;
}

interface Address {
  city: string;
  country: string;
  email: string;
  firstName: string;
  lastName: string;
  postalCode: string;
  streetAddress: string;
}

interface Item {
  name: string;
  quantity: number;
  totalPrice: number;
  pricePerUnit: number;
}

interface InvoiceNode {
  dbId: number;
  chargeStatus: string;
  note: string;
  dueDate: string;
  alias: string;
  billingAddress: Address;
  shippingAddress: Address;
  createdAt: string;
  status: string;
  paymentTerm: string;
  items: Item[];
  totalTax: number;
  totalShippingCost: number;
  totalPriceWithoutTax: number;
  totalDiscountedAmount: number;
  totalPrice: number;
}

interface InvoiceData {
orderInvoices: InvoiceNode[];
invoiceSetting: {
    city: string;
    contactEmail: string;
    contactPhone: string;
    country: string;
    logo: string;
    postalCode: string;
    storeAddress: string;
    storeName: string;
  };
}


const GET_INVOICES = gql`
    query MyQuery($orderIds: [Int!]!, $invoiceSettingsId: ID) {
        orderInvoices(orderIds: $orderIds) {
            chargeStatus
            note
            dbId
            dueDate
            alias
            billingAddress {
                city
                country
                email
                firstName
                lastName
                postalCode
                streetAddress
            }
            createdAt
            status
            paymentTerm
            shippingAddress {
                city
                country
                email
                firstName
                lastName
                postalCode
                streetAddress
            }
            items {
                name
                quantity
                pricePerUnit
                totalPrice
            }
            totalTax
            totalShippingCost
            totalPriceWithoutTax
            totalDiscountedAmount
            totalPrice
        }
        invoiceSetting(invoiceSettingsId: $invoiceSettingsId) {
            city
            contactEmail
            contactPhone
            country
            logo
            postalCode
            storeAddress
            storeName
        }
    }
`;


const formatValue = (value: string | number | null | undefined): string => {
  return value !== null && value !== undefined ? String(value) : "N/A";
};

const generateBulkInvoice = (data: InvoiceData) => {
    const doc = new jsPDF();
    const { invoiceSetting, orderInvoices } = data;
    
    
    const loadLogoAndCreatePage = (index: number) => {
        return new Promise((resolve) => {
            // Add new page for subsequent invoices
            if (index > 0) doc.addPage();
            
            if (!invoiceSetting.logo) {
                createInvoicePage(index, 15); // Start at default margin if no logo
                resolve(true);
                return;
            }

            const img = new Image();
            img.crossOrigin = "Anonymous";
            
            img.onload = () => {
                // Calculate dimensions while maintaining aspect ratio
                const imgWidth = 20;
                const imgHeight = (img.height * imgWidth) / img.width;
                
                // Add logo
                doc.addImage(
                    img,
                    invoiceSetting.logo.toLowerCase().endsWith('.png') ? 'PNG' : 'JPEG',
                    15, // Left margin
                    15, // Top margin
                    imgWidth,
                    imgHeight
                );

                // Position header content next to logo
                createInvoicePage(index, 45); // Start content after logo
                resolve(true);
            };

            img.onerror = () => {
                console.error('Error loading logo image');
                createInvoicePage(index, 15);
                resolve(true);
            };

            img.src = invoiceSetting.logo;
        });
    };

    const createInvoicePage = (index: number, startX: number) => {
        const headerY = 20;
        const invoice = orderInvoices[index];

        // Header Section
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(formatValue(invoiceSetting.storeName), startX, headerY);

        // Store Information
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text([
            formatValue(invoiceSetting.storeAddress),
            `${formatValue(invoiceSetting.city)}, ${formatValue(invoiceSetting.country)} ${formatValue(invoiceSetting.postalCode)}`,
            `Tel: ${formatValue(invoiceSetting.contactPhone)}`,
            `Email: ${formatValue(invoiceSetting.contactEmail)}`
        ], startX, headerY + 10);

        // Invoice Details (Right-aligned)
        const rightMargin = 195;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('INVOICE', rightMargin, headerY, { align: 'right' });
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text([
            `Invoice #: ${formatValue(invoice.dbId)}`,
            `Date: ${new Date(invoice.createdAt).toLocaleDateString()}`,
            invoice.dueDate ? `Due Date: ${new Date(invoice.dueDate).toLocaleDateString()}` : '',
            // `Status: ${formatValue(invoice.status)}`
        ], rightMargin, headerY + 10, { align: 'right' });

        // Addresses Section
        const addressY = 65;
        createAddressSection(doc, invoice, addressY);

        // Items Table
        createItemsTable(doc, invoice, addressY + 45);

        // Add footer if needed
        if (invoice.note) {
            const footerY = (doc as any).lastAutoTable.finalY + 10;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'italic');
            doc.text(`Note: ${formatValue(invoice.note)}`, 15, footerY);
        }
    };

    const createAddressSection = (doc: jsPDF, invoice: InvoiceNode, startY: number) => {
        // Billing Address
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('BILL TO', 15, startY);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text([
            `${formatValue(invoice.billingAddress?.firstName)} ${formatValue(invoice.billingAddress?.lastName)}`,
            formatValue(invoice.billingAddress?.streetAddress),
            `${formatValue(invoice.billingAddress?.city)}, ${formatValue(invoice.billingAddress?.country)}`,
            `${formatValue(invoice.billingAddress?.postalCode)}`,
            `Email: ${formatValue(invoice.billingAddress?.email)}`
        ], 15, startY + 7);

        // Shipping Address
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('SHIP TO', 110, startY);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text([
            `${formatValue(invoice.shippingAddress?.firstName)} ${formatValue(invoice.shippingAddress?.lastName)}`,
            formatValue(invoice.shippingAddress?.streetAddress),
            `${formatValue(invoice.shippingAddress?.city)}, ${formatValue(invoice.shippingAddress?.country)}`,
            `${formatValue(invoice.shippingAddress?.postalCode)}`,
            `Email: ${formatValue(invoice.shippingAddress?.email)}`
        ], 110, startY + 7);
    };

    const createItemsTable = (doc: jsPDF, invoice: InvoiceNode, startY: number) => {
        (doc as any).autoTable({
            startY: startY,
            head: [['Item', 'Quantity', 'Price/Unit', 'Total']],
            body: invoice.items.map((item) => [
                formatValue(item.name),
                formatValue(item.quantity),
                `${formatValue(item.pricePerUnit)}`,
                `${formatValue(item.totalPrice)}`
            ]),
            headStyles: { fillColor: [70, 70, 70] },
            foot: [
                ['', '', 'Subtotal:', `${formatValue(invoice.totalPriceWithoutTax)}`],
                ['', '', 'Shipping:', `${formatValue(invoice.totalShippingCost)}`],
                ['', '', 'Tax:', `${formatValue(invoice.totalTax)}`],
                ['', '', 'Discount:', `-${formatValue(invoice.totalDiscountedAmount)}`],
                ['', '', 'Total:', `${formatValue(invoice.totalPrice)}`]
            ],
            footStyles: { fillColor: [255, 255, 255] },
            theme: 'striped',
            margin: { left: 15, right: 15 }
        });
    };

    const processInvoices = async () => {
        for (let i = 0; i < orderInvoices.length; i++) {
            await loadLogoAndCreatePage(i);
        }

        const pdfBlob = doc.output("blob");
        const blobUrl = URL.createObjectURL(pdfBlob);
        const newWindow = window.open(blobUrl, "_blank");

        if (newWindow) {
            newWindow.onload = () => URL.revokeObjectURL(blobUrl);
        }
    };

    processInvoices();
};


export { generateBulkInvoice, GET_INVOICES };